import dayjs from "dayjs";

import airportTags from "@/constants/airportTags";

import { FilterData, FilterSourceData, FilterSourceItem } from "../typing";
import { getBanksByProgram } from "../utils";
import { UnknownAirlineKey } from "../utils/constants";

const programsFilterFn = (data: FilterSourceItem, value: FilterData["programs"]): boolean => {
  if (value.length === 0) {
    return true;
  }
  return value.includes(data.program);
};

const banksFilterFn = (data: FilterSourceItem, value: FilterData["banks"]): boolean => {
  if (value.length === 0) {
    return true;
  }
  const banks = getBanksByProgram(data.program);
  return banks.some((v) => value.includes(v));
};

const cabinsFilterFn = (data: FilterSourceItem, value: FilterData["cabins"]): boolean => {
  if (value.length === 0) {
    return true;
  }

  if (data.nonstop) {
    return false;
  }

  return value.some((key) => {
    if (key === "Economy") {
      return data.target_cabin === "y";
    } else if (key === "PremiumEconomy") {
      return data.target_cabin === "w";
    } else if (key === "First") {
      return data.target_cabin === "f";
    } else if (key === "Business") {
      return data.target_cabin === "j";
    }
  });
};

const nonStopFilterFn = (data: FilterSourceItem, value: FilterData["nonStop"]): boolean => {
  if (value.length === 0) {
    return true;
  }

  if (!data.nonstop) {
    return false;
  }

  return value.some((key) => {
    if (key === "Economy") {
      return data.target_cabin === "y";
    } else if (key === "PremiumEconomy") {
      return data.target_cabin === "w";
    } else if (key === "First") {
      return data.target_cabin === "f";
    } else if (key === "Business") {
      return data.target_cabin === "j";
    }
  });
};

const taxFilterFn = (data: FilterSourceItem, value: FilterData["tax"]): boolean => {
  if (value === -1) {
    return true;
  }
  return data.tax <= value;
};

const categoryFilterFn = (data: FilterSourceItem, value: FilterData["category"]): boolean => {
  if (value.length === 0) {
    return true;
  }
  return Array.from(
    new Set(
      airportTags
        .filter((item) => value.includes(item.name))
        .map((item) => item.airports)
        .flat()
    )
  ).some((v) => data.route.split("-")[1] === v);
};

const seatsFilterFn = (data: FilterSourceItem, value: FilterData["seats"]): boolean => {
  if (value === 1) {
    return true;
  }
  return data.ss >= value;
};

const premiumFilterFn = (data: FilterSourceItem, value: FilterData["premium"]): boolean => {
  if (value === 0) {
    return true;
  }
  return data.c_p >= value;
};

const maxPointsFilterFn = (data: FilterSourceItem, value: FilterData["maxPoints"]): boolean => {
  if (value === -1) {
    return true;
  }
  return data.price > 0 && value >= data.price;
};

const lastSeenTimeFilterFn = (
  data: FilterSourceItem,
  value: FilterData["lastSeenTime"]
): boolean => {
  if (!value.length) {
    return true;
  }

  const diff = dayjs().diff(dayjs(data.ls * 1000), "second");

  if (diff <= Number(value)) {
    return true;
  }

  return false;
};

const airlinesFilterFn = (data: FilterSourceItem, filter: FilterData): boolean => {
  const { airlines } = filter;

  let result = false;
  if (airlines.length === 0) {
    return true;
  }

  const airlineNames = data.c_a;

  for (let index = 0; index < airlineNames.length; index++) {
    const airlineName = airlineNames[index] ?? "";

    if (airlines.includes(airlineName)) {
      result = true;
    }
  }

  if (!airlineNames.length) {
    if (airlines.includes(UnknownAirlineKey)) {
      result = true;
    }
  }

  return result;
};

const excludeAirlinesFilterFn = (data: FilterSourceItem, filter: FilterData): boolean => {
  const { excludeAirlines } = filter;

  let result = false;
  if (excludeAirlines.length === 0) {
    return true;
  }

  const airlineNames = data.c_a;

  for (let index = 0; index < airlineNames.length; index++) {
    const airlineName = airlineNames[index] ?? "";

    if (excludeAirlines.includes(airlineName)) {
      result = true;
    }
  }

  if (!airlineNames.length) {
    if (excludeAirlines.includes(UnknownAirlineKey)) {
      result = true;
    }
  }

  return !result;
};
/**
 * 根据过滤条件筛选数据源
 * @param list
 * @param filter
 * @returns
 */
export const useFilterList = (list: FilterSourceData, filter: FilterData): FilterSourceData => {
  return list.filter((item) => {
    return [
      programsFilterFn(item, filter["programs"]),
      banksFilterFn(item, filter["banks"]),
      cabinsFilterFn(item, filter["cabins"]),
      nonStopFilterFn(item, filter["nonStop"]),
      taxFilterFn(item, filter["tax"]),
      categoryFilterFn(item, filter["category"]),
      seatsFilterFn(item, filter["seats"]),
      premiumFilterFn(item, filter["premium"]),
      maxPointsFilterFn(item, filter["maxPoints"]),
      lastSeenTimeFilterFn(item, filter["lastSeenTime"]),
      airlinesFilterFn(item, filter),
      excludeAirlinesFilterFn(item, filter),
    ].every(Boolean);
  });
};
